<template>
    <div ref="chartContainer" :class="containerClass"></div>
</template>

<script>
import * as echarts from "echarts";
import { generateBlueColorPalette } from "../../utils/colorUtils.js";

export default {
    props: {
        data: {
            type: Array,
            default: () => [],
        },
        config: {
            type: Array,
            default: () => [],
        },
        activeTab: {
            type: Number,
            default: 0,
        },
        xAxisUnit: {
            type: String,
            default: "monthly",
        },
        containerClass: {
            type: String,
            default: "chart-container",
        },
        height: {
            type: String,
            default: "250px",
        },
        customers: {
            type: Array,
            default: () => [],
        },
        selectedPeriod: {
            type: String,
            default: "monthly",
        },
        customerDataField: {
            type: String,
            default: "usage",
        },
        tooltipUnit: {
            type: String,
            default: "kWh",
        },
        yAxisName: {
            type: String,
            default: null,
        },
        yAxisFormatter: {
            type: Function,
            default: null,
        },
        boundaryGap: {
            type: Boolean,
            default: undefined,
        },
    },
    data() {
        return {
            mainColor: "#BDBDBD",
            chart: null,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.initChart();
            window.addEventListener("resize", this.handleResize);
        });
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        if (this.chart) {
            this.chart.dispose();
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.initChart();
                    }
                });
            },
            deep: true,
        },
        activeTab: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.initChart();
                    }
                });
            },
        },
        config: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.initChart();
                    }
                });
            },
            deep: true,
        },
    },
    methods: {
        initChart() {
            // 차트가 이미 초기화되어 있다면 제거
            if (this.chart) {
                this.chart.dispose();
            }

            if (!this.$refs.chartContainer) {
                return;
            }

            this.chart = echarts.init(this.$refs.chartContainer);

            // 데이터가 없으면 빈 차트 표시
            if (!this.data || this.data.length === 0) {
                return;
            }

            const option = this.buildChartOption();
            this.chart.setOption(option);
        },

        buildChartOption() {
            const baseOption = {
                backgroundColor: "transparent",
                tooltip: this.buildTooltip(),
                legend: this.buildLegend(),
                grid: this.buildGrid(),
                xAxis: this.buildXAxis(),
                yAxis: this.buildYAxis(),
                series: this.buildSeries(),
            };

            return baseOption;
        },

        buildTooltip() {
            // customers가 있으면 특별한 포맷 사용
            if (this.customers && this.customers.length > 0) {
                // tooltipUnit prop이 있으면 사용, 없으면 기본값
                const unit = this.tooltipUnit || "kWh";
                return {
                    trigger: "axis",
                    appendTo: "body",
                    axisPointer: {
                        type: "shadow",
                    },
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderColor: "#e2e8f0",
                    borderWidth: 1,
                    textStyle: {
                        color: "#1e293b",
                    },
                    formatter: (params) => {
                        if (!params || params.length === 0) return "";
                        let title = `<div style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; font-size: 12px; color: #1e293b;">${params[0].axisValue}</div>`;
                        let body = '<div style="padding: 8px 12px;">';
                        params.forEach((param) => {
                            const value = typeof param.value === "object" ? param.value.value : param.value;
                            const valueStr =
                                value != null ? value.toLocaleString() + (unit === "원" ? "원" : " " + unit) : "-";
                            body += `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 11px;">
                                <span style="display: flex; align-items: center; color: #64748b;">${param.marker}<span style="margin-left: 4px;">${param.seriesName}</span></span>
                                <span style="font-weight: 700; color: #1e293b; margin-left: 16px;">${valueStr}</span>
                            </div>`;
                        });
                        body += "</div>";
                        return title + body;
                    },
                };
            }

            return {
                trigger: "axis",
                appendTo: "body",
                axisPointer: {
                    type: "shadow",
                },
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderColor: "#e2e8f0",
                borderWidth: 1,
                textStyle: {
                    color: "#1e293b",
                },
                formatter: (params) => {
                    if (!params || params.length === 0) return "";

                    const axisValue = params[0].axisValue;
                    let title = `<div style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; font-size: 12px; color: #1e293b;">${echarts.format.encodeHTML(axisValue)}</div>`;
                    let body = '<div style="padding: 8px 12px;">';

                    // 0이 아니고 null이 아닌 값만 툴팁에 표시
                    params.forEach((param) => {
                        const value = typeof param.value === "object" ? param.value.value : param.value;

                        if (value != null && value > 0) {
                            const marker = param.marker;
                            let seriesName = param.seriesName;

                            // colorField가 있는 단일 시리즈인 경우, 포인트별로 예측 여부 확인
                            // (이미 "(예측)"이 이름에 포함된 경우는 제외)
                            if (!seriesName.includes("(예측)")) {
                                const config = this.config.find((c) => c.name === seriesName);
                                if (config && config.colorField) {
                                    const dataIndex = param.dataIndex;
                                    const dataItem = this.data[dataIndex];
                                    if (dataItem && dataItem[config.colorField]) {
                                        seriesName = `${seriesName} (예측)`;
                                    }
                                }
                            }

                            const valueStr = this.formatTooltipValue(value, param.seriesType);
                            body += `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 11px;">
                                <span style="display: flex; align-items: center; color: #64748b;">${marker}<span style="margin-left: 4px;">${echarts.format.encodeHTML(seriesName)}</span></span>
                                <span style="font-weight: 700; color: #1e293b; margin-left: 16px;">${valueStr}</span>
                            </div>`;
                        }
                    });

                    body += "</div>";
                    return title + body;
                },
            };
        },

        buildLegend() {
            let legendData = this.config.map((item) => item.name) || [];

            // customers가 있으면 legend에 추가
            if (this.customers && this.customers.length > 0) {
                const customerNames = this.customers.map((c) => c.custNm);
                legendData = [...legendData, ...customerNames];
            }

            if (legendData.length === 0) {
                legendData = ["데이터"];
            }

            return {
                data: legendData,
                textStyle: {
                    color: this.mainColor,
                    fontSize: 11,
                },
                bottom: 2,
                itemWidth: 12,
                itemHeight: 8,
            };
        },

        buildGrid() {
            return {
                left: "3%",
                right: "3%",
                top: "9%",
                height: "80%",
                containLabel: true,
            };
        },

        buildXAxis() {
            const xAxisData = this.getXAxisData();
            const rotate = this.selectedPeriod === "monthly" && this.data[0] && this.data[0].date ? 45 : 0;
            return {
                type: "category",
                data: xAxisData,
                boundaryGap: this.boundaryGap !== undefined ? this.boundaryGap : true,
                axisLabel: {
                    color: "#64748b",
                    fontSize: 10,
                    rotate: rotate,
                },
                axisLine: {
                    lineStyle: {
                        color: "#e2e8f0",
                    },
                },
            };
        },

        buildYAxis() {
            // config에서 yAxis 설정 추출
            const yAxisConfigs = this.config.filter((item) => item.yAxis).map((item) => item.yAxis);

            if (yAxisConfigs.length > 0) {
                return yAxisConfigs.map((axisConfig, index) => {
                    return {
                        type: "value",
                        position: index === 0 ? "left" : "right",
                        splitNumber: 5, // 그리드 라인 5개로 제한
                        axisLabel: {
                            color: this.mainColor,
                            formatter: axisConfig.formatter || ((value) => value.toLocaleString()),
                        },
                        axisLine: {
                            lineStyle: {
                                color: this.mainColor,
                            },
                        },
                        splitLine: {
                            show: index === 0, // 왼쪽 y축만 그리드 라인 표시
                            lineStyle: {
                                color: this.mainColor,
                            },
                        },
                        min: axisConfig.min,
                        max: axisConfig.max,
                    };
                });
            }

            // 기본 단일 y축
            // yAxisName prop이 있으면 사용, 없으면 기본값
            const yAxisName = this.yAxisName || "사용량 (kWh)";
            const yAxisFormatter =
                this.yAxisFormatter ||
                ((value) => {
                    if (value >= 1000) {
                        return (value / 1000).toFixed(1) + "k";
                    }
                    return value.toLocaleString();
                });

            return {
                type: "value",
                name: yAxisName,
                position: "left",
                nameTextStyle: {
                    color: "#64748b",
                    fontSize: 11,
                },
                axisLabel: {
                    color: "#64748b",
                    fontSize: 10,
                    formatter: yAxisFormatter,
                },
                axisLine: {
                    lineStyle: {
                        color: "#e2e8f0",
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: "dashed",
                        color: "#e2e8f0",
                    },
                },
            };
        },

        buildSeries() {
            const series = [];

            // customers가 있고 customerDataField가 설정된 경우 동적 라인차트 생성
            if (this.customers && this.customers.length > 0 && this.customerDataField) {
                const colorPalette = generateBlueColorPalette(this.customers.length);

                this.customers.forEach((customer, index) => {
                    const customerColor = colorPalette[index] || "rgb(100, 150, 200)";
                    const customerData = this.data.map((item) => {
                        const dataArray = item[this.customerDataField] || [];
                        return dataArray[customer.index] || 0;
                    });

                    series.push({
                        name: customer.custNm || `고객 ${index + 1}`,
                        type: "line",
                        data: customerData,
                        smooth: true,
                        lineStyle: {
                            width: 2,
                            color: customerColor,
                            type: "solid",
                        },
                        itemStyle: {
                            color: customerColor,
                        },
                        emphasis: {
                            focus: "series",
                        },
                        symbol: "circle",
                        symbolSize: 5,
                    });
                });
            }

            this.config.forEach((config) => {
                let seriesData = this.getSeriesDataByField(config.dataField, config);

                // highlightItem이 있으면 각 데이터 항목을 객체로 변환하여 itemStyle 적용
                if (config.highlightItem !== undefined && seriesData.length > 0) {
                    const highlightColor = config.highlightColor || "#3b82f6";
                    const defaultColor = config.color || "#3b82f6";
                    const borderRadius = config.borderRadius || [10, 10, 0, 0];

                    seriesData = seriesData.map((value, index) => {
                        if (value === null || value === undefined) {
                            return null;
                        }
                        const isHighlighted = index === config.highlightItem;
                        return {
                            value: value,
                            itemStyle: {
                                color: isHighlighted ? highlightColor : defaultColor,
                                borderRadius: borderRadius,
                            },
                        };
                    });
                }

                // overlap이 true인 경우 예측 데이터를 먼저 추가 (배경)
                const predField = config.predField;
                if (config.overlap && predField) {
                    const predSeriesData = this.getSeriesDataByField(predField);
                    const hasPredData = predSeriesData.some((val) => val != null && val > 0);

                    if (hasPredData) {
                        const predSeries = {
                            name: `${config.name} (예측)`,
                            type: config.type || "bar",
                            data: predSeriesData,
                            barWidth: config.barWidth || "40%",
                            barGap: "-100%", // 바를 겹치게 함
                            yAxisIndex: config.yAxisIndex || 0,
                            itemStyle: {
                                color: config.predColor || "#94a3b8",
                                borderRadius: config.borderRadius || [10, 10, 0, 0],
                                opacity: config.predOpacity || 0.5,
                            },
                            lineStyle: {
                                ...(config.lineStyle || {}),
                                type: "solid",
                            },
                            areaStyle: config.areaStyle || {},
                            smooth: config.smooth || false,
                            z: 1, // 배경으로
                        };
                        series.push(predSeries);
                    }
                }

                // 실제 데이터 시리즈
                const baseSeries = {
                    name: config.name,
                    type: config.type || "bar",
                    data: seriesData,
                    barWidth: config.barWidth || "40%",
                    yAxisIndex: config.yAxisIndex || 0,
                    itemStyle:
                        config.highlightItem === undefined
                            ? {
                                  color: config.color || "#3b82f6",
                                  borderRadius: config.borderRadius || [10, 10, 0, 0],
                              }
                            : undefined, // highlightItem이 있으면 data의 itemStyle 사용
                    lineStyle: config.lineStyle || {},
                    ...(config.areaStyle !== undefined
                        ? config.areaStyle === null
                            ? {}
                            : { areaStyle: config.areaStyle }
                        : {}),
                    smooth: config.smooth || false,
                    symbol: config.symbol !== undefined ? config.symbol : undefined,
                    label: config.label || undefined,
                    z: 2, // 위에 표시
                };
                series.push(baseSeries);

                // overlap이 false인 경우 예측 데이터를 나중에 추가 (나란히 표시)
                if (!config.overlap && predField) {
                    const predSeriesData = this.getSeriesDataByField(predField);
                    const hasPredData = predSeriesData.some((val) => val != null && val > 0);

                    if (hasPredData) {
                        const predSeries = {
                            name: `${config.name} (예측)`,
                            type: config.type || "bar",
                            data: predSeriesData,
                            barWidth: config.barWidth || "40%",
                            yAxisIndex: config.yAxisIndex || 0,
                            itemStyle: {
                                color: config.predColor || "#94a3b8",
                                borderRadius: config.borderRadius || [10, 10, 0, 0],
                                opacity: 0.6,
                            },
                            lineStyle: {
                                ...(config.lineStyle || {}),
                                type: "solid",
                            },
                            areaStyle: config.areaStyle || {},
                            smooth: config.smooth || false,
                        };
                        series.push(predSeries);
                    }
                }
            });

            return series;
        },

        getXAxisData() {
            if (!this.data) return [];

            // date 필드가 있는 경우 (DashboardUsageTrendChart 스타일)
            if (this.data[0] && this.data[0].date) {
                return this.data.map((item) => this.formatDate(item.date));
            }

            if (this.xAxisUnit === "daily") {
                return this.data.map((item) => `${parseInt(item.regDt.substring(6, 8))}일`);
            }
            if (this.xAxisUnit === "monthly") {
                return this.data.map((item) => `${parseInt(item.regDt.substring(4, 6))}월`);
            } else if (this.xAxisUnit === "yearly") {
                return this.data.map((item) => `${parseInt(item.regDt.substring(0, 4))}년`);
            }

            // regDt가 없는 경우 다른 필드 사용
            return this.data.map((item, index) => `항목 ${index + 1}`);
        },

        formatDate(dateStr) {
            if (!dateStr) return "";
            if (this.selectedPeriod === "monthly") {
                // YYYYMMDD 형식인 경우
                if (dateStr.length === 8) {
                    const month = parseInt(dateStr.substring(4, 6));
                    const day = parseInt(dateStr.substring(6, 8));
                    return `${month}/${day}`;
                }
                // YYYYMM 형식인 경우
                else if (dateStr.length === 6) {
                    const year = dateStr.substring(0, 4);
                    const month = parseInt(dateStr.substring(4, 6));
                    return `${year}년 ${month}월`;
                }
            } else {
                // YYYY 형식인 경우
                if (dateStr.length === 4) {
                    return `${dateStr}년`;
                }
                // YYYYMM 형식인 경우
                else if (dateStr.length === 6) {
                    const month = parseInt(dateStr.substring(4, 6));
                    return `${month}월`;
                }
            }
            return dateStr;
        },

        getSeriesData() {
            if (!this.data) return [];

            // 기본 로직: cost 필드 사용
            if (this.data[0] && this.data[0].cost !== undefined) {
                return this.data.map((item) => item.cost);
            }

            // reqAmt 필드 사용
            if (this.data[0] && this.data[0].reqAmt !== undefined) {
                return this.data.map((item) => item.reqAmt);
            }

            return [];
        },

        getSeriesDataByField(fieldName, config) {
            if (!this.data || !fieldName) return [];

            // colorField가 있으면 각 포인트마다 다른 색상 적용
            if (config && config.colorField) {
                return this.data.map((item) => {
                    // null은 그대로 유지 (차트에서 렌더링 안 함)
                    const value = item[fieldName] === null ? null : (item[fieldName] ?? 0);
                    const colorFlag = item[config.colorField];

                    // null인 경우 null 반환
                    if (value === null) {
                        return null;
                    }

                    // colorFlag에 따라 색상 결정
                    let color = config.color || "#3b82f6";
                    if (colorFlag && config.predColor) {
                        color = config.predColor;
                    }

                    return {
                        value: value,
                        itemStyle: {
                            color: color,
                            borderRadius: config.borderRadius || [10, 10, 0, 0],
                        },
                    };
                });
            }

            // null은 그대로 유지, undefined는 0으로 변환
            return this.data.map((item) => (item[fieldName] === null ? null : (item[fieldName] ?? 0)));
        },

        handleResize() {
            if (this.chart) {
                this.chart.resize();
            }
        },
        resize() {
            this.handleResize();
        },

        formatTooltipValue(value, seriesType) {
            const num = Number(value);
            if (isNaN(num)) return value;

            // 라인 차트는 소수점 포함, 바 차트는 천 단위 구분
            if (seriesType === "line") {
                return num.toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
            }

            return num.toLocaleString("ko-KR");
        },
    },
};
</script>

<style scoped>
.chart-container {
    height: v-bind(height);
    width: 100%;
    min-height: v-bind(height);
    flex: 1;
    background: transparent;
    border-radius: 8px;
}
</style>
